import React from "react";
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";

type UserDataInterface = {
  initialized: boolean;
  loggedIn: boolean;
  user: any;
  userMetadata: Object;
};
type MyContextInterface = {
  authInfo: UserDataInterface;
  initialize: () => Promise<boolean>;
  logOut: () => Promise<boolean>;
  logIn: (email: string, password: string) => Promise<boolean>;
};

// create the context
export const AuthContext = React.createContext<MyContextInterface | undefined>(
  undefined
);

// create the context provider, we are using use state to ensure that
// we get reactive values from the context...
type Props = {
  children: React.ReactNode;
};
export const AuthProvider: React.FC = (props: any) => {
  const auth = getAuth();
  // the reactive values
  const [authInfo, setAuthInfo] = React.useState<UserDataInterface>();

  const logOut = () => {
    return new Promise((resolve) => {
      signOut(auth)
        .then(() => {
          // Sign-out successful.
          window.localStorage.removeItem("AUTH");
          setAuthInfo({
            initialized: true,
            loggedIn: false,
            user: null,
            userMetadata: {},
          });
        })
        .catch((error) => {
          // An error happened.
          //   console.log("signOut error: " + error);
        });
    });
  };

  const logIn = (email: string, password: string) => {
    return new Promise((resolve) => {
      signInWithEmailAndPassword(auth, email, password)
        .then(async (userCredential) => {
          // Signed in
          let userDocSnap = await getDoc(doc(db, "users", email));

          if (userDocSnap.exists()) {
            // console.log("User data:", userDocSnap.data());
            setAuthInfo({
              initialized: true,
              loggedIn: true,
              user: { email, id: new Date().getTime() + "" },
              userMetadata: userDocSnap.data(),
            });
            window.localStorage.setItem(
              "AUTH",
              JSON.stringify({
                initialized: true,
                loggedIn: true,
                user: { email, id: new Date().getTime() + "" },
                userMetadata: userDocSnap.data(),
              })
            );
          } else {
            // doc.data() will be undefined in this case
            // console.log("No such user!");
            setAuthInfo({
              initialized: true,
              loggedIn: false,
              user: { email, id: new Date().getTime() + "" },
              userMetadata: {},
            });
          }
          resolve(true);
        })
        .catch((error) => {
          //   console.log("Error logging: " + JSON.stringify(error));
          resolve(false);
        });
    });
  };

  const initialize = () => {
    let response = window.localStorage.getItem("AUTH") || null;
    if (response !== null) {
      setAuthInfo({
        initialized: true,
        loggedIn: true,
        user: response !== "undefined" ? JSON.parse(response).user : null,
        userMetadata:
          response !== "undefined" ? JSON.parse(response).userMetadata : {},
      });
    } else {
      setAuthInfo({
        initialized: true,
        loggedIn: false,
        user: null,
        userMetadata: {},
      });
    }
  };

  let v = {
    authInfo,
    logOut: logOut,
    logIn: logIn,
    initialize,
  };

  return <AuthContext.Provider value={v} {...props} />;
};

export const useAuth = () => React.useContext(AuthContext);
